import clsx from "clsx";
import { useState, useEffect, useRef, ReactNode } from "react";

import { FieldLabel } from "./FieldLabel";
import { View } from ".";
import { Icon } from "./Icon";

type DropdownFieldProps = {
  name?: string;
  options: Array<{
    label: string | ReactNode;
    value: string;
    displayLabel?: string | ReactNode;
    searchValue?: string;
  }>;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  labelClassName?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  readOnly?: boolean;
  tooltip?: React.ReactNode;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
};

export const DropdownField = ({
  name,
  options = [],
  value,
  onChange = () => {},
  className,
  label,
  placeholder,
  error,
  labelClassName,
  readOnly = false,
  tooltip,
  tooltipPlacement = "top",
}: DropdownFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownFieldRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectedOption = options.find((opt) => opt.value === value);
  const filteredOptions = options.filter((option) => {
    if (!search) return true;
    const searchText = option.searchValue || (typeof option.label === "string" ? option.label : "");
    return searchText.toLowerCase().includes(search.toLowerCase());
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownFieldRef.current && !dropdownFieldRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          onClickOptionHandler(filteredOptions[highlightedIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const onClickOptionHandler = (value: string) => {
    onChange(value);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <FieldLabel
      label={label}
      className={clsx("w-full", className)}
      error={error}
      tooltip={tooltip}
      tooltipPlacement={tooltipPlacement}
    >
      <View
        className={clsx("relative w-full", error && "animate-shake")}
        ref={dropdownFieldRef}
      >
        <View
          className={clsx(
            "flex items-center px-4 h-10 w-full border rounded-lg",
            "text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800",
            "focus-within:outline-none focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-2 dark:focus-within:ring-offset-gray-900",
            !readOnly && "cursor-pointer",
            {
              "border-gray-300 dark:border-gray-600": !error,
              "border-error dark:border-error-light": error,
            },
          )}
          onClick={() => (readOnly ? null : setIsOpen(!isOpen))}
        >
          <View className="flex-1 flex items-center">
            {isOpen ? (
              <input
                name={name}
                type="text"
                className="w-full h-8 border-none bg-transparent outline-none text-sm"
                placeholder={placeholder}
                autoFocus
                value={search}
                onChange={onChangeInput}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <View className={clsx("text-sm", !selectedOption && "text-gray-400 dark:text-gray-500")}>
                {selectedOption?.displayLabel || selectedOption?.label || placeholder || "Select an option"}
              </View>
            )}
          </View>
          <Icon
            name={isOpen ? "chevron-up" : "chevron-down"}
            size={16}
            className="text-gray-500 dark:text-gray-400"
          />
        </View>

        {isOpen && (
          <View
            className={clsx(
              "absolute z-50 w-full mt-2 border rounded-lg shadow-lg overflow-y-auto max-h-60",
              // Use higher-contrast surface in dark mode so options are visible
              "bg-white text-gray-900",
              "dark:bg-gray-900 dark:text-gray-100",
              error ? "border-error dark:border-error-light" : "border-gray-200 dark:border-gray-700",
            )}
          >
            {filteredOptions.length === 0 ? (
              <View className="p-3 text-sm text-gray-500 dark:text-gray-400">No options found</View>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  ref={(el) => (optionRefs.current[index] = el)}
                  onClick={() => onClickOptionHandler(option.value)}
                  className={clsx(
                    "w-full p-3",
                    "cursor-pointer transition-colors duration-150",
                    // Improve hover/selected contrast in dark mode
                    "hover:bg-gray-50 dark:hover:bg-gray-800/80",
                    value === option.value && "bg-gray-100 dark:bg-gray-800",
                    highlightedIndex === index && "bg-gray-100 dark:bg-gray-800",
                    labelClassName,
                  )}
                >
                  {option.label}
                </div>
              ))
            )}
          </View>
        )}
      </View>
    </FieldLabel>
  );
};
